/* ─────────────────────────────────────────────
   RECORDING — record UI + watchPosition loop (Cluster 9)
   updateRecordUi, tickRecording, onGeoError, startRecording, stopRecording
   ───────────────────────────────────────────── */
import { $ } from './dom';
import { fmtRelative } from './format';
import { showToast } from './toast';
import { state } from '../state/app-state';
import { MOCK } from '../state/device';
import { drawRoutes } from '../map/tracker';
import { renderSessions } from './sidebar';
import { apiPostPings, apiStartSession, apiStopSession } from '../api/client';
import { loadStoredToken, saveActiveSession, clearActiveSession } from '../storage/persistence';

// Callback injected by main.ts so ui/recording.ts can call ensureRegistered()
// without importing main.ts (would be a circular dep once the module is split).
let _ensureRegisteredCb: ((token: string) => Promise<string>) | null = null;
export function setEnsureRegisteredCb(fn: (token: string) => Promise<string>): void { _ensureRegisteredCb = fn; }

const GEO_ERROR_COPY: Record<string, string> = {
  denied:      'location permission denied',
  unavailable: 'location unavailable',
  timeout:     'location request timed out',
  unsupported: 'this browser does not support geolocation',
};
const GEO_HELP_URL = 'https://developer.mozilla.org/docs/Web/API/Geolocation_API#permissions';

let startSessionInFlight = false;
let geolocationWatchId: number | null = null;
let lastRecPing: number | null = null;

export function getLastRecPing(): number | null { return lastRecPing; }

export function updateRecordUi(){
  const strip = $('record-strip');
  const label = $('record-label');
  const status= $('status-text');
  if(!strip || !label || !status) return;
  const compact = window.matchMedia('(max-width: 860px)').matches;

  // Cadence text is computed locally rather than read from a DOM element,
  // because `#status-text`'s innerHTML is rewritten on every call and any
  // child `#cadence-label` element gets nuked between renders. (Reading
  // back from it broke the cancel flow: the requesting/error branches
  // didn't include the child, so the next updateRecordUi threw on
  // `cad.textContent` and the UI froze mid-update.)
  const cadenceText = state.cadenceSec < 60
    ? `${state.cadenceSec} s`
    : `${state.cadenceSec/60} min`;

  if(state.geoRequesting){
    strip.dataset.rec = 'on';
    label.textContent = compact ? '…' : 'cancel';
    status.innerHTML = compact ? 'waiting…' : 'waiting for location…';
    return;
  }
  if(state.recording){
    strip.dataset.rec = 'on';
    label.textContent = 'stop';
    status.innerHTML = compact
      ? `Rec &middot; <b>${cadenceText}</b>${lastRecPing ? ' &middot; ' + fmtRelative(lastRecPing) : ''}`
      : `Recording &middot; every <b>${cadenceText}</b> &middot; last ping ${lastRecPing ? fmtRelative(lastRecPing) : 'just now'}`;
    return;
  }
  strip.dataset.rec = 'off';
  label.textContent = compact ? 'record' : 'start recording';
  if(state.geoError){
    const copy = GEO_ERROR_COPY[state.geoError] || 'location error';
    status.innerHTML = compact
      ? `${copy} &middot; <a href="${GEO_HELP_URL}" target="_blank" rel="noopener">help</a>`
      : `${copy} &middot; <a href="${GEO_HELP_URL}" target="_blank" rel="noopener">how to re-enable</a>`;
  } else {
    status.innerHTML = compact
      ? `Off &middot; <b>${cadenceText}</b>`
      : `Not recording &middot; cadence <b>${cadenceText}</b>`;
  }
}

// Called from navigator.geolocation.watchPosition on each new fix.
// Throttles by state.cadenceSec, then persists.
export function tickRecording(position: any){
  state.geoRequesting = false;
  const now = position.timestamp || Date.now();
  if(lastRecPing !== null && (now - lastRecPing) < state.cadenceSec * 1000){
    // Surplus fix — drop. The watch keeps firing; we only accept on cadence.
    return;
  }
  const acc = position.coords?.accuracy;
  // Browser geolocation altitude is WGS-84 ellipsoidal, not MSL, and far
  // less reliable than the horizontal fix (ADR 0013). We store it as-is —
  // there is no geoid model in the browser — and lean on
  // altitude_accuracy_meters + the elevation view's low-confidence framing.
  const alt = position.coords?.altitude;
  const altAcc = position.coords?.altitudeAccuracy;
  const next = {
    t: now,
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    precision_meters: Number.isFinite(acc) ? acc : null,
    altitude_meters: typeof alt === 'number' && Number.isFinite(alt) ? alt : null,
    altitude_accuracy_meters:
      typeof altAcc === 'number' && Number.isFinite(altAcc) ? altAcc : null,
    session_id: state.activeSession ? state.activeSession.id : null,
  };
  MOCK.pings.push(next);
  lastRecPing = next.t;

  // Optimistic cohort update for the local device: don't wait up to 30s
  // for the next poll to reflect this ping in the by-device tab.
  if(MOCK.device){
    const entry = state.cohort.get(MOCK.device.token);
    if(entry){
      entry.pings.push(next);
      entry.lastT = next.t;
    }
  }

  // Fire-and-forget persistence. No retry queue in V1; if a tick is lost,
  // the next one still records and the gap shows up as another session.
  if(MOCK.device && loadStoredToken()){
    apiPostPings(MOCK.device.token, [next]).catch(err => {
      const status = err?.status;
      console.warn('[locus] ping persistence failed', status || err?.code, err?.message);
      // Self-heal stale session: server rejects the batch because the
      // session_id we tagged the ping with no longer exists or is closed
      // server-side. Most common cause: localStorage carrying an
      // activeSession from a prior deploy where the row was wiped. Drop
      // it locally so the next tick can resume in null-session mode.
      const errMsg = err?.body?.error || '';
      if(status === 400 && errMsg.startsWith('unknown or closed session_id') && state.activeSession){
        const staleId = state.activeSession.id;
        state.activeSession = null;
        if(MOCK.device) clearActiveSession(MOCK.device.token);
        console.warn('[locus] cleared stale session', staleId);
        showToast('session reset — server lost track');
      } else if(status === 401 && !(tickRecording as any)._toldAuth){
        (tickRecording as any)._toldAuth = true;
        showToast('signing key missing — pings not saved');
      } else if(status === 429 && !(tickRecording as any)._toldRate){
        (tickRecording as any)._toldRate = true;
        showToast('rate limited — slowing down');
      }
    });
  }

  // Newest session is at id 's0' (groupSessions reverses); keep it focused.
  state.selectedSession = 's0';
  drawRoutes();
  renderSessions();
  updateRecordUi();
}

export function onGeoError(err: any){
  const map: Record<number, string> = {
    1: 'denied',         // PERMISSION_DENIED
    2: 'unavailable',    // POSITION_UNAVAILABLE
    3: 'timeout',        // TIMEOUT
  };
  state.geoError = map[err?.code] || 'unavailable';
  console.warn('[locus] geolocation error', err?.code, err?.message);
  stopRecording();
}

export async function startRecording(){
  if(state.recording || state.geoRequesting || startSessionInFlight) return;
  if(!navigator.geolocation){
    state.geoError = 'unsupported';
    updateRecordUi();
    return;
  }
  state.geoError = null;
  state.geoRequesting = true;
  state.recording = true;
  // Reset the cadence-throttle anchor so the first fix of a new session
  // lands immediately. Without this, lastRecPing persists from the prior
  // session and silently drops fixes for up to `cadenceSec` seconds
  // (visible as "start recording doesn't do anything").
  lastRecPing = null;
  updateRecordUi();

  // If the device was minted offline, make sure it has a server row before
  // we open a session / post pings — otherwise both would 404. No-op for
  // normally-created devices; may adopt a new token on collision.
  if(MOCK.device) await (_ensureRegisteredCb ? _ensureRegisteredCb(MOCK.device.token) : Promise.resolve(MOCK.device.token));

  // Open a server-tracked session unless one is already active (resume on
  // page reload). startSessionInFlight guards the double-tap race: the
  // server's auto-close-then-open is correct regardless, but holding the
  // flag avoids needless extra rows.
  if(MOCK.device && !state.activeSession){
    startSessionInFlight = true;
    try {
      const res = await apiStartSession(MOCK.device.token, Date.now(), null);
      state.activeSession = { id: res.session_id, started_at: res.started_at };
      saveActiveSession(MOCK.device.token, state.activeSession);
    } catch (err) {
      console.warn('[locus] session-start failed', err);
      // Non-fatal: pings still post with session_id: null and segment by
      // gap clustering. UX feels normal; surface a quiet toast.
      showToast('session boundary not saved');
    } finally {
      startSessionInFlight = false;
    }
  }

  geolocationWatchId = navigator.geolocation.watchPosition(
    tickRecording,
    onGeoError,
    { enableHighAccuracy: true, maximumAge: 0, timeout: 30_000 }
  );
}

export function stopRecording(){
  if(geolocationWatchId !== null){
    try { navigator.geolocation.clearWatch(geolocationWatchId); } catch {}
    geolocationWatchId = null;
  }
  state.recording = false;
  state.geoRequesting = false;
  updateRecordUi();

  // Close the server-tracked session if one is open. Fire-and-forget —
  // stop is idempotent server-side (404 if already closed), so a missed
  // /stop call from a flaky network just means ended_at stays NULL until
  // the next /start auto-closes it.
  if(MOCK.device && state.activeSession){
    const sess = state.activeSession;
    state.activeSession = null;
    clearActiveSession(MOCK.device.token);
    apiStopSession(MOCK.device.token, sess.id, Date.now()).catch(err => {
      // 404 here just means the server already considers the session
      // closed (or never knew about it — stale localStorage from a
      // prior deploy). Local state is already cleared above; nothing
      // to do. Log louder for non-404 cases.
      const msg = String(err?.message || err);
      if(msg.includes('404')) return;
      console.warn('[locus] session-stop failed', err);
    });
  }
}
